import React from "react";
import { cn } from "@/lib/utils";
import Breadcrumbs, { BreadcrumbsProps } from "@/components/ui/breadcrumbs";
import AnchorListItems, {
  AnchorListItemsProps,
} from "@/components/ui/anchor-list-items";
import PrimaryButton, {
  PrimaryButtonProps,
} from "@/components/ui/primary-button";
import SecondaryButton, {
  SecondaryButtonProps,
} from "@/components/ui/secondary-button";
import Image, { ImageProps } from "next/image";
import HeaderImage from "@/assets/header-image.jpeg";

export interface HeaderProps {
  id?: string;
  className?: string;
  headingClassName?: string;
  heading?: string;
  subheading?: string;
  description?: string;
  showPrimaryCTA?: boolean;
  primaryCTA?: PrimaryButtonProps;
  secondaryCTA?: SecondaryButtonProps;
  showSecondaryCTA?: boolean;
  breadcrumbs?: BreadcrumbsProps;
  showAnchors?: boolean;
  anchors?: AnchorListItemsProps;
  image?: ImageProps;
}
const Header: React.FC<HeaderProps> = ({
  id = "header",
  className,
  headingClassName = "",
  heading = "Heading",
  subheading = "Subheading",
  description = "Lorem ipsum dolor sit amet consectetur. Ultrices risus mi venenatis ut cursus vestibulum faucibus cras morbi. Risus sed pretium vel leo nunc natoque.",
  breadcrumbs,
  showPrimaryCTA = true,
  primaryCTA = {
    color: "orange",
    className: "w-fit",
    children: "Become Our Partner",
    href: "#",
  },
  showSecondaryCTA = true,
  secondaryCTA = {
    className: "w-fit",
    color: "teal",
    children: "Learn More",
    href: "#",
  },
  showAnchors = true,
  anchors,
  image = {
    src: HeaderImage,
    alt: "Header Image",
  },
}) => {
  return (
    <section
      id={id}
      aria-label={id}
      className={cn(
        "mx-auto grid w-full max-w-lg grid-cols-2 gap-2 bg-white p-4 md:gap-4 md:px-6 md:py-8 lg:gap-x-8 lg:gap-y-6 lg:px-3 lg:py-10",
        className
      )}
    >
      <div className="col-span-full">
        {breadcrumbs && <Breadcrumbs {...breadcrumbs} />}
      </div>
      <div className="col-span-full flex w-full flex-col md:gap-8 lg:col-span-1 lg:flex-row lg:gap-12">
        <div className="flex flex-col gap-4 lg:w-[624px] lg:gap-6">
          <hgroup className="flex flex-col gap-2 lg:gap-4">
            <h1
              className={cn(
                "font-montserrat text-heading-3 text-secondary-500 md:text-heading-2 lg:text-heading-1",
                headingClassName
              )}
            >
              {heading}
            </h1>
            <p className="font-montserrat text-subheading-3-400 text-neutral-500 md:text-subheading-2 lg:text-subheading-1">
              {subheading}
            </p>
          </hgroup>
          <p className="font-roboto text-body-1-300 text-neutral-500">
            {typeof description === "string" ? (
              <div
                className="rich-text [&_a]:hover:text-primary-700 [&_a]:focus:text-primary-700 [&_a]:rounded-md [&_a]:font-roboto [&_a]:text-body-1-400 [&_a]:text-primary-600 [&_a]:underline [&_a]:underline-offset-2 [&_a]:hover:no-underline [&_a]:focus:no-underline [&_a]:focus:outline [&_a]:focus:outline-1 [&_a]:focus:outline-offset-2 [&_a]:focus:outline-black"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            ) : (
              description
            )}
          </p>
          <div className="flex flex-row gap-3 md:gap-4 lg:gap-6">
            {showPrimaryCTA && primaryCTA && (
              <PrimaryButton
                color="orange"
                size="md"
                {...primaryCTA}
                id={primaryCTA.id ?? "primary-cta-subpage-header"}
              />
            )}
            {showSecondaryCTA && secondaryCTA && (
              <SecondaryButton
                color="teal"
                size="md"
                {...secondaryCTA}
                id={secondaryCTA.id ?? "secondary-cta-subpage-header"}
              />
            )}
          </div>
        </div>
      </div>
      {image && (
        <div className="order-last col-span-full flex flex-col justify-center lg:order-none lg:col-span-1">
          <Image
            className={cn(
              "rounded-3xl shadow-[4px_4px_0px_0px_#FFD5BF] md:shadow-[6px_6px_0px_0px_#FFD5BF] lg:shadow-[8px_8px_0px_0px_#FFD5BF]",
              "w-full md:max-h-[400px] lg:max-w-[600px]",
              "aspect-[358/200] md:aspect-[536/300] lg:aspect-[600/324]",
              "bg-center object-cover"
            )}
            {...image}
            alt={image?.alt}
            width={800}
            height={500}
            priority
          />
        </div>
      )}

      <div className="col-span-full">
        {showAnchors && anchors && <AnchorListItems {...anchors} />}
      </div>
    </section>
  );
};

export default Header;
